import React, { useEffect, useState } from 'react';

interface StepRevealProps {
  onComplete: () => void;
}

interface RevealData {
  netWorth: number;
  totalAssets: number;
  totalLiabilities: number;
  assetCount: number;
  liabilityCount: number;
}

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

const formatCurrency = (n: number) =>
  new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  }).format(Math.abs(n));

function useCountUp(target: number, duration = 1400): number {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (target === 0) { setValue(0); return; }
    const start = Date.now();
    let raf: number;
    const tick = () => {
      const elapsed = Date.now() - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(target * eased));
      if (progress < 1) raf = requestAnimationFrame(tick);
      else setValue(target);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return value;
}

const authHeaders = (): HeadersInit => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

const StepReveal: React.FC<StepRevealProps> = ({ onComplete }) => {
  const [data, setData] = useState<RevealData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [historyRes, assetsRes, liabilitiesRes] = await Promise.all([
          fetch(`${API_BASE}/accounts/history/networth?days=30`, { headers: authHeaders() }),
          fetch(`${API_BASE}/accounts/breakdown/category?type=asset`, { headers: authHeaders() }),
          fetch(`${API_BASE}/accounts/breakdown/category?type=liability`, { headers: authHeaders() }),
        ]);

        if (!historyRes.ok || !assetsRes.ok || !liabilitiesRes.ok) {
          throw new Error('Failed to fetch');
        }

        const [history, assetBreakdown, liabilityBreakdown] = await Promise.all([
          historyRes.json(),
          assetsRes.json(),
          liabilitiesRes.json(),
        ]);

        // Use most recent history entry for totals; fall back to breakdown sums
        const latest = Array.isArray(history) && history.length > 0
          ? history[history.length - 1]
          : null;

        const totalAssets = latest
          ? parseFloat(latest.total_assets || '0')
          : (assetBreakdown as { total_value: number }[]).reduce((s, r) => s + r.total_value, 0);

        const totalLiabilities = latest
          ? parseFloat(latest.total_liabilities || '0')
          : (liabilityBreakdown as { total_value: number }[]).reduce((s, r) => s + r.total_value, 0);

        const netWorth = latest
          ? parseFloat(latest.net_worth || '0')
          : totalAssets - totalLiabilities;

        const assetCount = (assetBreakdown as { count: number }[]).reduce((s, r) => s + r.count, 0);
        const liabilityCount = (liabilityBreakdown as { count: number }[]).reduce((s, r) => s + r.count, 0);

        setData({ netWorth, totalAssets, totalLiabilities, assetCount, liabilityCount });
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  // Must be called before any early returns to satisfy Rules of Hooks
  const animatedNetWorth = useCountUp(data?.netWorth ?? 0);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4">
        <div className="w-8 h-8 rounded-full border-2 border-purple-500 border-t-transparent animate-spin" />
        <p className="text-sm text-white/50">Calculating your net worth...</p>
      </div>
    );
  }

  const cta = (
    <button
      type="button"
      onClick={onComplete}
      className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-4 rounded-xl font-semibold text-lg mt-8 transition-colors"
    >
      Take me to my dashboard →
    </button>
  );

  if (error || !data) {
    return (
      <div className="flex flex-col items-center text-center gap-4 mt-16">
        <p className="text-sm text-white/50">
          Head to your dashboard to see your full picture.
        </p>
        {cta}
      </div>
    );
  }

  const { netWorth, totalAssets, totalLiabilities, assetCount, liabilityCount } = data;

  const netWorthColor =
    netWorth > 0 ? 'text-green-400' : netWorth < 0 ? 'text-red-400' : 'text-white';

  const nothingEntered = assetCount === 0 && liabilityCount === 0;

  const encouragement = nothingEntered
    ? "Add your first accounts from the dashboard to see your real number."
    : netWorth > 0
    ? "You're already ahead of most Canadians who've never calculated this number."
    : "You have a starting point. Every dollar you track from here moves that number.";

  return (
    <div className="flex flex-col items-center text-center" style={{ animation: 'revealFadeIn 0.6s ease both' }}>
      <style>{`
        @keyframes revealFadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {nothingEntered ? (
        <>
          <p className="text-3xl font-bold text-white mb-3">Your dashboard is ready.</p>
          <p className="text-base text-white/50 mb-8 max-w-md text-center">
            You haven't added any accounts yet — that's okay. Head to your dashboard and start with whatever you know offhand. Even one number makes this useful.
          </p>
        </>
      ) : (
        <>
          <p className="text-base text-white/50 mb-3">Your net worth, right now</p>
          <p className={`text-6xl font-bold mb-1 ${netWorthColor}`}>
            {netWorth < 0 ? '-' : ''}{formatCurrency(animatedNetWorth)}
          </p>
          <p className="text-base text-white/30 mb-6">CAD</p>
          <div className="flex gap-4 w-full mt-2">
            <div className="flex-1 rounded-xl border border-white/10 bg-white/5 p-4 text-center">
              <p className="text-sm text-white/40 mb-1">Assets</p>
              <p className="text-xl font-semibold text-white">{formatCurrency(totalAssets)}</p>
              <p className="text-sm text-white/30 mt-1">across {assetCount} account{assetCount !== 1 ? 's' : ''}</p>
            </div>
            <div className="flex-1 rounded-xl border border-white/10 bg-white/5 p-4 text-center">
              <p className="text-sm text-white/40 mb-1">Liabilities</p>
              <p className="text-xl font-semibold text-white">{formatCurrency(totalLiabilities)}</p>
              <p className="text-sm text-white/30 mt-1">across {liabilityCount} account{liabilityCount !== 1 ? 's' : ''}</p>
            </div>
          </div>
          <p className="text-base text-white/50 mt-6 max-w-md text-center">{encouragement}</p>
        </>
      )}

      {!nothingEntered && (
        <div className="w-full mt-8 rounded-xl border border-white/10 bg-white/5 p-5 text-left">
          <p className="text-sm font-medium text-white mb-4">Make this number more accurate</p>
          <ul className="space-y-3">
            {[
              { icon: '📈', text: 'Add the rest of your assets — investments, real estate, savings' },
              { icon: '💳', text: 'Add any debts you haven\'t entered yet — loans, credit cards, mortgage' },
              { icon: '🏢', text: 'Add your business equity if you haven\'t already' },
            ].map(({ icon, text }) => (
              <li key={text} className="flex items-start gap-3">
                <span className="text-lg shrink-0">{icon}</span>
                <p className="text-sm text-white/60">{text}</p>
              </li>
            ))}
          </ul>
          <p className="text-xs text-white/30 mt-4">The more you add, the more useful your dashboard becomes.</p>
        </div>
      )}

      {cta}

      <p className="text-sm text-white/30 mt-3">
        Your data updates as you add and connect accounts.
      </p>
    </div>
  );
};

export default StepReveal;
